import { buildMakeUseAxios } from "@api";
import { useCallback } from "react";

const axios = buildMakeUseAxios(`${import.meta.env.VITE_WORKSHOP_SERVICE_URL}`);

export const useCreateSession = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/v2/sessions",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data) => {
      return execute({
        url: "/v2/sessions",
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePatchSession = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/v2/sessions",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id, data) => {
      return execute({
        url: `/v2/sessions/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useUpdateSession = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/v2/sessions",
      method: "PUT",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id, data) => {
      return execute({
        url: `/v2/sessions/${id}`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetSessions = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/v2/sessions/search",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (query) => {
      return execute({
        url: `/v2/sessions/search?${new URLSearchParams(query).toString()}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetSession = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/v2/sessions",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (sessionId) => {
      return execute({
        url: `/v2/sessions/${sessionId}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useCloseSession = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/v2/sessions/:id/close",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id, data) => {
      return execute({
        url: `/v2/sessions/${id}/close`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useDeleteSession = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/v2/sessions/:id",
      method: "DELETE",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id) => {
      return execute({
        url: `/v2/sessions/${id}`,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useDeleteSessionParticipants = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/v2/sessions/:id/participants",
      method: "DELETE",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id, participantsIds) => {
      return execute({
        url: `/v2/sessions/${id}/participants`,
        data: { ids: participantsIds },
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useRegenerateSms = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/v2/sessions/:id/applies/regenerate-sms",
      method: "PATCH",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id, data) => {
      return execute({
        url: `/v2/sessions/${id}/applies/regenerate-sms`,
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGenerateSms = () => {
  const [state, execute, cancel] = axios<undefined, Record<string, string>, unknown>(
    {
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (id: string, templates: Record<string, string>) =>
      execute({ url: `/v2/sessions/${id}/generate-sms`, data: templates }),
    [execute]
  );

  return [state, callback, cancel] as const;
};
